import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../lib/auth/useAuth';
import { useTimer } from '../../lib/timer/TimerContext';
import { TextLink } from '../ui/TextLink';
import { Button } from '../ui/Button';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { LogActivityButton } from '../activity/LogActivityButton';
import { MatterSelector } from '../matter/MatterSelector';
import type { SelectedMatter } from '../matter/MatterSelector';
import { formatElapsed } from './format-elapsed';

// ---------------------------------------------------------------------------
// Running timer display
// ---------------------------------------------------------------------------

function ActiveTimer() {
  const { elapsed, isPaused, autoPaused, matterId, clientId, matterName, clientName, saveTimer, pauseTimer, resumeTimer, discardTimer } = useTimer();
  const navigate = useNavigate();
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  // Capture elapsed at dialog open time so the message doesn't jitter every second
  const [discardElapsed, setDiscardElapsed] = useState(0);

  const handleSave = () => {
    // Pause (don't clear) so the user can resume if they cancel the wizard.
    // The timer is only fully cleared when the activity is successfully saved.
    if (!isPaused) pauseTimer();
    saveTimer();
    navigate('/activities');
  };

  const handleDiscardClick = () => {
    setDiscardElapsed(elapsed);
    setShowDiscardConfirm(true);
  };

  return (
    <>
      <div className="space-y-2.5">
        {/* Timer display */}
        <div className="rounded-lg border border-border bg-bg-primary px-3 py-2.5">
          {/* Status indicator + context */}
          <div className="mb-2 flex items-center gap-2">
            {isPaused ? (
              <span className="inline-flex h-2.5 w-2.5 shrink-0 rounded-full bg-warning" />
            ) : (
              <span className="relative flex h-2.5 w-2.5 shrink-0">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-success opacity-75" />
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-success" />
              </span>
            )}
            <div className="min-w-0 flex-1">
              {matterId ? (
                <TextLink to={`/matters/${matterId}`} tone="sentence" className="block truncate text-sm">
                  {matterName}
                </TextLink>
              ) : (
                <div className="truncate text-sm font-medium text-secondary">{matterName}</div>
              )}
              {clientId ? (
                <TextLink to={`/clients/${clientId}`} tone="sentence" className="block truncate text-xs">
                  {clientName}
                </TextLink>
              ) : (
                <div className="truncate text-xs text-text-muted">{clientName}</div>
              )}
            </div>
          </div>

          {/* Elapsed time */}
          <div
            className={`text-center text-2xl font-semibold tabular-nums tracking-wide ${isPaused ? 'text-warning' : 'text-primary'}`}
            aria-live="polite"
            aria-label={`Timer: ${formatElapsed(elapsed)}${isPaused ? ' (paused)' : ''}`}
          >
            {formatElapsed(elapsed)}
          </div>
          {isPaused && (
            <div className="mt-1 text-center text-xs text-warning">
              {autoPaused ? 'Auto-paused after 24h' : 'Paused'}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          {isPaused ? (
            <Button size="sm" onClick={resumeTimer} className="flex-1">
              Resume
            </Button>
          ) : (
            <Button variant="secondary" size="sm" onClick={pauseTimer} className="flex-1">
              Pause
            </Button>
          )}
          <Button size="sm" onClick={handleSave} className="flex-1">
            Save
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleDiscardClick}
            className="text-text-muted hover:text-error"
            aria-label="Discard timer"
          >
            <TrashIcon className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Rendered outside space-y-2.5 to prevent layout shift from the gap selector */}
      <ConfirmDialog
        open={showDiscardConfirm}
        title="Discard timer"
        message={`Discard ${formatElapsed(discardElapsed)} of tracked time for ${matterName}? This can't be undone.`}
        confirmLabel="Discard"
        cancelLabel="Keep timing"
        variant="danger"
        onConfirm={() => {
          discardTimer();
          setShowDiscardConfirm(false);
        }}
        onCancel={() => setShowDiscardConfirm(false)}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Main widget
// ---------------------------------------------------------------------------

export function TimerWidget() {
  const { accountId } = useAuth();
  const { hasTimer, startTimer } = useTimer();
  const [showMatterPicker, setShowMatterPicker] = useState(false);

  const handleMatterSelect = (matter: SelectedMatter) => {
    startTimer(matter.id, matter.clientId, matter.name, matter.clientName);
    setShowMatterPicker(false);
  };

  return (
    <div className="mx-3 border-t border-border/70 py-3 space-y-2.5">
      {hasTimer ? (
        <>
          {/* Log activity button sits above the active timer panel */}
          <LogActivityButton fullWidth />
          <ActiveTimer />
        </>
      ) : showMatterPicker ? (
        <>
          {/* Log activity button sits above the matter picker */}
          <LogActivityButton fullWidth />
          <MatterSelector
            accountId={accountId!}
            onSelect={handleMatterSelect}
            onCancel={() => setShowMatterPicker(false)}
            autoFocus
          />
        </>
      ) : (
        /* Idle state: Log activity (primary) above Start timer (secondary). */
        <div className="space-y-2">
          <LogActivityButton fullWidth />
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => setShowMatterPicker(true)}
            aria-label="Start timer"
            className="w-full"
          >
            <TimerIcon className="h-3.5 w-3.5 shrink-0" />
            <span>Start timer</span>
          </Button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Icons
// ---------------------------------------------------------------------------

function TimerIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

function TrashIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
      />
    </svg>
  );
}
